import { useEffect, useMemo, useCallback, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  FeatureGroup,
  Polygon,
  useMap,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { useSelector, useDispatch, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet-draw";
import "../../i18n/leafletDraw";
import {
  clearDrawnShape,
  clearTargetBounds,
  hideTileBan,
  setCurrentField,
  setDrawnShape,
  setMapCenter,
  setMapZoom,
  setRestoreView,
} from "../../reducers/MapSlice";
import { useTileTemplateRefresh } from "../../hooks/useTileTemplateRefresh";
import "./styles.css";
import { url } from "../../api/axios_client";
import { queryClient } from "../../api/queryClient";
import { fieldKeys, ownerKeys } from "../../api/queryKeys";
import {
  getFields,
  fieldsAck,
  getFieldMeta,
  getOwnerMeta,
} from "../../api/FlaskAPI";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import AppTheme from "../../components/AppTheme";
import { FieldCard, OwnerCard, PopupError } from "../../components/MapPopup";
import TileBanDialog from "../../components/TileBanDialog";
import { LAYERS_BASE_Z_INDEX } from "../../constants/tilesconstants";

// Fields are refetched only this long after the map stops moving.
const FIELDS_FETCH_DEBOUNCE_MS = 200;

// Minimal debounce: runs fn only after `wait` ms without another call.
function debounce(fn, wait) {
  let timeoutId = null;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
}

// Casual deterrent only: suppress the browser context menu over the map so
// saving/copying tile images takes more than a right-click.
function MapContextMenuGuard() {
  const map = useMap();

  useEffect(() => {
    const handler = (e) => e.originalEvent.preventDefault();
    map.on("contextmenu", handler);
    return () => {
      map.off("contextmenu", handler);
    };
  }, [map]);

  return null;
}

// Component to manage map view restoration and fitting to target bounds
function MapView() {
  const dispatch = useDispatch();
  const store = useStore();
  const map = useMap();
  const restoreView = useSelector((state) => state.mapState.restoreView);
  const targetBounds = useSelector((state) => state.mapState.targetBounds);

  useEffect(() => {
    if (!targetBounds && !restoreView) return;

    if (targetBounds) {
      // Fit the map to the target bounds
      map.fitBounds(targetBounds);
    } else {
      // Restore the map to the last known center and zoom
      const { mapCenter, mapZoom } = store.getState().mapState;
      if (mapCenter && mapZoom) {
        map.setView(mapCenter, mapZoom);
      }
    }

    if (targetBounds) {
      dispatch(clearTargetBounds());
    }
    if (restoreView) {
      dispatch(setRestoreView(false));
    }
  }, [dispatch, map, store, targetBounds, restoreView]);

  useEffect(() => {
    // Save zoom and center on changes
    const handleMoveEnd = () => {
      dispatch(setMapZoom(map.getZoom()));
      dispatch(setMapCenter({ ...map.getCenter() }));
    };

    map.on("moveend", handleMoveEnd);
    map.on("zoomend", handleMoveEnd);

    return () => {
      map.off("moveend", handleMoveEnd);
      map.off("zoomend", handleMoveEnd);
    };
  }, [map, dispatch]);

  return null;
}

function DrawHandler({ drawingMode, drawnShape, boundaryMethod }) {
  const dispatch = useDispatch();

  const _onCreated = (e) => {
    let layer = e.layer;

    let coordinates;

    // Handle different shape types
    if (layer instanceof L.Circle) {
      // For circles, we need to convert to a polygon with proper lat/lng coordinates
      const center = layer.getLatLng();
      const radius = layer.getRadius(); // in meters
      const points = 64; // number of points to approximate circle

      coordinates = [];
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * 2 * Math.PI;
        // Calculate offset in meters
        const dx = radius * Math.cos(angle);
        const dy = radius * Math.sin(angle);

        // Convert meters to degrees (approximate)
        // 1 degree latitude ≈ 111,320 meters
        // 1 degree longitude ≈ 111,320 * cos(latitude) meters
        const latOffset = dy / 111320;
        const lngOffset =
          dx / (111320 * Math.cos((center.lat * Math.PI) / 180));

        const lat = center.lat + latOffset;
        const lng = center.lng + lngOffset;

        coordinates.push([lng, lat]); // GeoJSON format: [lng, lat]
      }
    } else {
      // For rectangles, polygons, and other shapes
      const geoJson = layer.toGeoJSON();
      coordinates = geoJson.geometry.coordinates[0];
    }

    dispatch(
      setDrawnShape({
        polyId: layer._leaflet_id,
        coordinates: coordinates,
      }),
    );
  };

  const _onEdited = (e) => {
    const layer = e.layers.getLayers()[0];

    let coordinates;

    if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      const radius = layer.getRadius();
      const points = 64;
      coordinates = [];
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * 2 * Math.PI;
        const dx = radius * Math.cos(angle);
        const dy = radius * Math.sin(angle);
        const latOffset = dy / 111320;
        const lngOffset =
          dx / (111320 * Math.cos((center.lat * Math.PI) / 180));
        coordinates.push([center.lng + lngOffset, center.lat + latOffset]);
      }
    } else {
      const geoJson = layer.toGeoJSON();
      coordinates = geoJson.geometry.coordinates[0];
    }

    dispatch(
      setDrawnShape({
        polyId: layer._leaflet_id,
        coordinates: coordinates,
      }),
    );
  };

  const _onDeleted = (e) => {
    const islayers = e.layers.getLayers().length !== 0;
    if (islayers) {
      dispatch(clearDrawnShape());
    }
  };

  if (drawingMode && boundaryMethod === "draw") {
    const editControlRenderKey = drawnShape
      ? "render_if_changed_1"
      : "render_if_changed_2";
    return (
      <FeatureGroup>
        <EditControl
          key={editControlRenderKey}
          position="topleft"
          onCreated={_onCreated}
          onEdited={_onEdited}
          onDeleted={_onDeleted}
          draw={{
            rectangle: drawnShape ? false : true,
            circle: drawnShape ? false : true,
            polyline: false,
            marker: false,
            polygon: drawnShape ? false : true,
            circlemarker: false,
          }}
        />
      </FeatureGroup>
    );
  }
}

function FitDrawnShapeBounds({ drawnShape }) {
  const map = useMap();

  useEffect(() => {
    if (
      !drawnShape ||
      !drawnShape.coordinates ||
      drawnShape.coordinates.length === 0
    ) {
      return;
    }

    try {
      const latLngs = drawnShape.coordinates.map(([lng, lat]) => [lat, lng]);
      const bounds = L.latLngBounds(latLngs);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    } catch (e) {
      console.error("Error fitting bounds to drawn shape:", e);
    }
  }, [map, drawnShape]);

  return null;
}

const FIELD_POPUP_OPTIONS = {
  className: "fm-popup fm-popup--field",
  autoPan: false,
};
const OWNER_POPUP_OPTIONS = {
  className: "fm-popup fm-popup--owner",
  autoPan: false,
};
const TIP_INSET = 30; // where the tip sits when the popup opens to one side

// Leaflet would pan the map to fit the popup; flip the popup instead.
// The side panels float over the map, so they count as its edges. On phones
// they span the width instead, and the top card counts as the top edge.
const placePopup = (map, popup, latlng) => {
  const el = popup.getElement();
  el.classList.remove("fm-popup--below", "fm-popup--right", "fm-popup--left");
  popup.options.offset = L.point(0, 7);
  popup.update();
  const box = el.getBoundingClientRect();
  const view = map.getContainer().getBoundingClientRect();
  const click = map.latLngToContainerPoint(latlng);
  const x = view.left + click.x;
  const y = view.top + click.y;
  const left = document
    .querySelector(".map-overlay--left")
    ?.getBoundingClientRect();
  const right = document
    .querySelector(".map-overlay--right")
    ?.getBoundingClientRect();
  const wide = (panel) => panel && panel.width > view.width / 2;
  const leftEdge = left && !wide(left) ? left.right : view.left;
  const rightEdge = right && !wide(right) ? right.left : view.right;
  const topEdge = wide(left) ? left.bottom : view.top;

  const below = box.top < topEdge;
  const side =
    x - box.width / 2 < leftEdge
      ? "right"
      : x + box.width / 2 > rightEdge
        ? "left"
        : null;

  el.classList.toggle("fm-popup--below", below);
  el.classList.toggle("fm-popup--right", side === "right");
  el.classList.toggle("fm-popup--left", side === "left");

  const shift = box.width / 2 - TIP_INSET;
  popup.options.offset = L.point(
    side === "right" ? shift : side === "left" ? -shift : 0,
    below ? y - box.top + 27 : 7,
  );
  popup.update();
};

// Opens at once with a placeholder card, then fills in the details. Not bindPopup:
// Leaflet would reopen the old, empty popup on the next click.
const showPopup = async (map, latlng, options, placeholder, loadContent) => {
  const container = document.createElement("div");
  const root = createRoot(container);
  // Leaflet measures the popup when it opens or updates, so render first.
  const render = (element) =>
    flushSync(() => root.render(<AppTheme>{element}</AppTheme>));

  render(placeholder);
  const popup = L.popup(options)
    .setLatLng(latlng)
    .setContent(container)
    .openOn(map);
  placePopup(map, popup, latlng);
  popup.once("remove", () => setTimeout(() => root.unmount(), 300));

  const isOpen = () => map.hasLayer(popup); // false once closed or replaced
  const content = await loadContent(isOpen);
  if (!isOpen()) return;
  render(content ?? <PopupError />);
  placePopup(map, popup, latlng);
};

// Cached like the rest of the app (30 s), so reopening is instant.
// Saving an owner or field clears it with the rest of their queries.
const ownersPopupContent = async (id, areaUnit) => {
  try {
    const properties = await queryClient.fetchQuery({
      queryKey: ownerKeys.meta(id, areaUnit),
      queryFn: () => getOwnerMeta(id, areaUnit).then((r) => r.data),
    });
    return <OwnerCard owner={properties} areaUnit={areaUnit} />;
  } catch (error) {
    console.error("Error resolving feature properties:", error);
    return null;
  }
};

const fieldsPopupContent = async (id, dispatch, areaUnit, isOpen) => {
  try {
    const properties = await queryClient.fetchQuery({
      queryKey: fieldKeys.meta(id, areaUnit),
      queryFn: () => getFieldMeta(id, areaUnit).then((r) => r.data),
    });
    // A late reply mustn't swap the panel to a field the user left.
    if (isOpen()) dispatch(setCurrentField(properties));
    return <FieldCard field={properties} areaUnit={areaUnit} />;
  } catch (error) {
    console.error("Error resolving feature properties:", error);
    return null;
  }
};

const OWNERS_STYLE = {
  fill: true,
  fillOpacity: 0,
  weight: 2,
  color: "#3b6f9e",
};

function GeoJSONLayers() {
  const map = useMap();
  const dispatch = useDispatch();

  const sessionKey = useSelector((state) => state.mapState.sessionKey);
  const fillVectors = useSelector((state) => state.mapState.fillVectors);
  const fillVectorsRef = useRef(fillVectors);
  const overlayMode = useSelector((state) => state.mapState.overlayMode);
  const overlayModeRef = useRef(overlayMode);
  // The click handlers below are created once, so they read the unit via a ref.
  const areaUnit = useSelector((state) => state.settings.areaUnit);
  const areaUnitRef = useRef(areaUnit);

  const resetRef = useRef(true);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const pointsFieldsLayerRef = useRef(null);
  const areasFieldsLayerRef = useRef(null);
  const ownersLayerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getAreaFieldsStyle = useCallback(
    (feature) => ({
      fill: true,
      fillOpacity: fillVectors ? 1 : 0,
      fillColor: feature.properties.color,
      weight: 2,
      color: fillVectors ? "#000000" : feature.properties.color,
    }),
    [fillVectors],
  );

  useEffect(() => {
    fillVectorsRef.current = fillVectors;
  }, [fillVectors]);

  useEffect(() => {
    areaUnitRef.current = areaUnit;
  }, [areaUnit]);

  // Re-apply area style when the fill toggle changes without recreating layers.
  useEffect(() => {
    areasFieldsLayerRef.current?.setStyle(getAreaFieldsStyle);
  }, [getAreaFieldsStyle]);

  useEffect(() => {
    if (overlayModeRef.current !== overlayMode) {
      resetRef.current = true;
      overlayModeRef.current = overlayMode;
    }
  }, [overlayMode]);

  // Create the persistent Leaflet GeoJSON layers once.
  // A shared Canvas renderer draws all vector paths (field points, field areas and owner areas)
  // on a single canvas instead of thousands of SVG elements.
  useEffect(() => {
    const canvasRenderer = L.canvas({ padding: 0.5 });
    canvasRenderer.addTo(map);

    pointsFieldsLayerRef.current = L.geoJSON(null, {
      renderer: canvasRenderer,
      pointToLayer: (feature, latlng) =>
        L.circleMarker(latlng, {
          renderer: canvasRenderer,
          radius: 6,
          fillColor: feature.properties.color,
          color: "#ffffff",
          weight: 1,
          opacity: 1,
          fillOpacity: 1,
        }),
      onEachFeature: (feature, layer) => {
        layer.on("click", (e) => {
          showPopup(
            map,
            e.latlng,
            FIELD_POPUP_OPTIONS,
            <FieldCard />,
            (isOpen) =>
              fieldsPopupContent(
                feature.id,
                dispatch,
                areaUnitRef.current,
                isOpen,
              ),
          );
        });
      },
    });

    areasFieldsLayerRef.current = L.geoJSON(null, {
      renderer: canvasRenderer,
      style: (feature) => ({
        fill: true,
        fillOpacity: fillVectorsRef.current ? 1 : 0,
        fillColor: feature.properties.color,
        weight: 2,
        color: fillVectorsRef.current ? "#000000" : feature.properties.color,
      }),
      onEachFeature: (feature, layer) => {
        layer.on("click", (e) => {
          showPopup(
            map,
            e.latlng,
            FIELD_POPUP_OPTIONS,
            <FieldCard />,
            (isOpen) =>
              fieldsPopupContent(
                feature.id,
                dispatch,
                areaUnitRef.current,
                isOpen,
              ),
          );
        });
      },
    });

    ownersLayerRef.current = L.geoJSON(null, {
      renderer: canvasRenderer,
      style: OWNERS_STYLE,
      onEachFeature: (feature, layer) => {
        layer.on("click", (e) => {
          showPopup(map, e.latlng, OWNER_POPUP_OPTIONS, <OwnerCard />, () =>
            ownersPopupContent(feature.id, areaUnitRef.current),
          );
        });
      },
    });

    return () => {
      if (ownersLayerRef.current) map.removeLayer(ownersLayerRef.current);
      if (pointsFieldsLayerRef.current)
        map.removeLayer(pointsFieldsLayerRef.current);
      if (areasFieldsLayerRef.current)
        map.removeLayer(areasFieldsLayerRef.current);
      map.removeLayer(canvasRenderer);
    };
  }, [map, dispatch]);

  const updateLayers = useCallback(
    (showMode, resetModeLayers, resetAllLayers, fields, owners) => {
      if (resetAllLayers) {
        pointsFieldsLayerRef.current?.clearLayers();
        areasFieldsLayerRef.current?.clearLayers();
        ownersLayerRef.current?.clearLayers();
      }

      if (showMode === "Hide") {
        if (map.hasLayer(ownersLayerRef.current)) {
          map.removeLayer(ownersLayerRef.current);
        }
        if (map.hasLayer(areasFieldsLayerRef.current)) {
          map.removeLayer(areasFieldsLayerRef.current);
        }
        if (map.hasLayer(pointsFieldsLayerRef.current)) {
          map.removeLayer(pointsFieldsLayerRef.current);
        }
        return;
      }

      if (showMode === "Points") {
        if (map.hasLayer(ownersLayerRef.current)) {
          map.removeLayer(ownersLayerRef.current);
        }
        if (map.hasLayer(areasFieldsLayerRef.current)) {
          map.removeLayer(areasFieldsLayerRef.current);
        }
        if (!map.hasLayer(pointsFieldsLayerRef.current)) {
          pointsFieldsLayerRef.current?.addTo(map);
        }

        if (resetModeLayers) {
          pointsFieldsLayerRef.current?.clearLayers();
        }
        if (fields) {
          pointsFieldsLayerRef.current?.addData(fields);
        }
        return;
      }

      if (showMode === "Areas") {
        if (!map.hasLayer(ownersLayerRef.current)) {
          ownersLayerRef.current?.addTo(map);
        }
        if (!map.hasLayer(areasFieldsLayerRef.current)) {
          areasFieldsLayerRef.current?.addTo(map);
        }
        if (map.hasLayer(pointsFieldsLayerRef.current)) {
          map.removeLayer(pointsFieldsLayerRef.current);
        }

        if (resetModeLayers) {
          areasFieldsLayerRef.current?.clearLayers();
          ownersLayerRef.current?.clearLayers();
        }
        if (owners) {
          ownersLayerRef.current?.addData(owners);
        }
        if (fields) {
          areasFieldsLayerRef.current?.addData(fields);
        }
        return;
      }
    },
    [map],
  );

  const fetchFields = useCallback(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    const bounds = map.getBounds();
    if (!bounds || !bounds.isValid()) return;

    if (resetRef.current) {
      areasFieldsLayerRef.current?.clearLayers();
      ownersLayerRef.current?.clearLayers();
      pointsFieldsLayerRef.current?.clearLayers();
    }

    const params = {
      session_id: sessionKey,
      reset: resetRef.current,
      minx: bounds.getWest(),
      miny: bounds.getSouth(),
      maxx: bounds.getEast(),
      maxy: bounds.getNorth(),
      zoom: map.getZoom(),
      color: overlayMode,
    };

    getFields(params, signal)
      .then((res) => {
        if (!isMountedRef.current || signal.aborted) return;

        if (resetRef.current) {
          resetRef.current = false;
        }
        const {
          show_mode: showMode,
          reset_mode_layers: resetModeLayers,
          reset_all_layers: resetAllLayers,
          fields,
          owners,
          pending_id,
        } = res.data;
        updateLayers(showMode, resetModeLayers, resetAllLayers, fields, owners);
        if (pending_id) {
          fieldsAck({
            session_id: sessionKey,
            pending_id: pending_id,
          }).catch(() => {
            console.error("Failed to ack fields");
          });
        }
      })
      .catch((err) => {
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
        if (!isMountedRef.current) return;
        console.error("Failed to load fields:", err);
      });
  }, [map, sessionKey, overlayMode, updateLayers]);

  const debouncedFetchFields = useMemo(
    () => debounce(fetchFields, FIELDS_FETCH_DEBOUNCE_MS),
    [fetchFields],
  );

  useEffect(() => {
    fetchFields();

    const handleViewChange = () => debouncedFetchFields();
    const handleInteractionStart = () => abortControllerRef.current?.abort();

    map.on("moveend", handleViewChange);
    map.on("zoomend", handleViewChange);
    map.on("movestart", handleInteractionStart);
    map.on("zoomstart", handleInteractionStart);

    return () => {
      map.off("moveend", handleViewChange);
      map.off("zoomend", handleViewChange);
      map.off("movestart", handleInteractionStart);
      map.off("zoomstart", handleInteractionStart);
      debouncedFetchFields.cancel();
      abortControllerRef.current?.abort();
    };
  }, [map, fetchFields, debouncedFetchFields]);

  return null;
}

function MapComponent({ effectiveLayer }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    handleIndexTileError,
    handleSatelliteTileError,
    handlePlanetTileError,
    handleDefaultMapTileError,
    checkBanStatus,
  } = useTileTemplateRefresh();

  const workspaces = useSelector((state) => state.mapState.workspaces);
  const currentWorkspace = useSelector(
    (state) => state.mapState.currentWorkspace,
  );
  const tiles = workspaces[currentWorkspace];
  const aoiLayer = useSelector((state) => state.mapState.staticLayers.aoi);
  const mapBounds = useSelector((state) => state.mapState.mapBounds);
  const satelliteLayer = useSelector((state) => state.mapState.satelliteLayer);
  const planetLayer = useSelector((state) => state.mapState.planetLayer);
  const defaultmapLayer = useSelector(
    (state) => state.mapState.defaultmapLayer,
  );
  const boundaryMethod = useSelector((state) => state.mapState.boundaryMethod);
  const drawingMode = useSelector((state) => state.mapState.drawingMode);
  const drawnShape = useSelector((state) => state.mapState.drawnShape);
  const tileBan = useSelector((state) => state.mapState.tileBan);
  const tilesLimits = useSelector((state) => state.mapState.tilesLimits);
  const fieldsLayerKey = useSelector((state) => state.mapState.fieldsLayerKey);

  const visibleTiles = useMemo(
    () => (tiles || []).filter((tile) => tile.visible !== false),
    [tiles],
  );

  if (!mapBounds) {
    return null;
  }

  const center = { lat: 19.55979, lng: 29.267578 };
  const bounds = L.latLngBounds(mapBounds[0], mapBounds[1]);

  // Allow scrolling a fixed distance past the project bounds so panning at
  // the edges feels less restrictive.
  const MAX_BOUNDS_BUFFER = 0.1; // degrees
  const maxBounds = L.latLngBounds(
    [
      bounds.getSouth() - MAX_BOUNDS_BUFFER,
      bounds.getWest() - MAX_BOUNDS_BUFFER,
    ],
    [
      bounds.getNorth() + MAX_BOUNDS_BUFFER,
      bounds.getEast() + MAX_BOUNDS_BUFFER,
    ],
  );

  return (
    <>
      <MapContainer
        bounds={bounds}
        maxBounds={maxBounds}
        center={center}
        minZoom={10}
        maxZoom={tilesLimits.max_zoom}
        zoomControl={false}
      >
        <MapView />
        <DrawHandler
          drawingMode={drawingMode}
          drawnShape={drawnShape}
          boundaryMethod={boundaryMethod}
        />
        <MapContextMenuGuard />
        <FitDrawnShapeBounds drawnShape={drawnShape} />

        {/* TODO: This may duplicate the drawn shape if it was drawn by the EditControl tools. */}
        {drawingMode && drawnShape && (
          <Polygon
            positions={drawnShape.coordinates.map(([lng, lat]) => [lat, lng])}
            pathOptions={{
              color: "#1976d2",
              weight: 2,
              fillColor: "#1976d2",
              fillOpacity: 0.2,
            }}
          />
        )}

        {/* Static AOI Layer - Blue polygons without overlay */}
        {aoiLayer && (
          <GeoJSON
            data={aoiLayer}
            style={{
              fillOpacity: 0,
              weight: 2,
              color: "#3b6f9e",
            }}
            interactive={false}
          />
        )}

        {/* Fields GeoJSON Layer - Polygons or points based on backend show_mode, clickable */}
        <GeoJSONLayers key={fieldsLayerKey} />

        {effectiveLayer === "planet" && planetLayer.tileUrl && (
          <TileLayer
            key={planetLayer.tileId}
            attribution={planetLayer.attribution}
            url={`${url}${planetLayer.tileUrl}`}
            bounds={mapBounds}
            maxNativeZoom={tilesLimits.max_zoom}
            maxZoom={tilesLimits.max_zoom}
            updateWhenIdle={true}
            keepBuffer={3}
            eventHandlers={{
              tileerror: handlePlanetTileError,
            }}
          />
        )}

        {effectiveLayer === "satellite" && satelliteLayer.tileUrl && (
          <TileLayer
            key={satelliteLayer.tileId}
            attribution={satelliteLayer.attribution}
            url={`${url}${satelliteLayer.tileUrl}`}
            bounds={mapBounds}
            maxNativeZoom={tilesLimits.max_zoom}
            maxZoom={tilesLimits.max_zoom}
            updateWhenIdle={true}
            keepBuffer={3}
            eventHandlers={{
              tileerror: handleSatelliteTileError,
            }}
          />
        )}

        {effectiveLayer === "defaultmap" && defaultmapLayer.tileUrl && (
          <TileLayer
            key={defaultmapLayer.tileId}
            attribution={defaultmapLayer.attribution}
            url={`${url}${defaultmapLayer.tileUrl}`}
            bounds={mapBounds}
            maxNativeZoom={tilesLimits.max_zoom}
            maxZoom={tilesLimits.max_zoom}
            updateWhenIdle={true}
            keepBuffer={3}
            eventHandlers={{
              tileerror: handleDefaultMapTileError,
            }}
          />
        )}

        {/* Image tile layers */}
        {!tileBan.banned &&
          visibleTiles.map(
            (tile, index) =>
              tile.tileUrl &&
              tile.bbox && (
                <TileLayer
                  crossOrigin="anonymous"
                  key={tile.tileId}
                  url={`${url}${tile.tileUrl}`}
                  bounds={tile.bbox}
                  // first in the list gets the highest zIndex.
                  zIndex={visibleTiles.length - 1 - index + LAYERS_BASE_Z_INDEX}
                  maxNativeZoom={tilesLimits.max_zoom}
                  maxZoom={tilesLimits.max_zoom}
                  updateWhenIdle={true}
                  updateWhenZooming={false}
                  keepBuffer={3}
                  eventHandlers={{
                    tileerror: handleIndexTileError,
                  }}
                />
              ),
          )}
      </MapContainer>

      <TileBanDialog
        open={!!tileBan.banned && !!tileBan.show}
        message={tileBan?.message || t("map.ban.fallback")}
        onClose={() => dispatch(hideTileBan())}
        onTryAgain={() => checkBanStatus()}
      />
    </>
  );
}

export default MapComponent;
